import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, BellOff, Loader2, Pencil, Trash2, Check, X, Mail, ArrowRight } from "lucide-react";
import api, { apiErr } from "../lib/api";

const WORK_MODES = [["", "Any work mode"], ["onsite", "On-site"], ["remote", "Remote"], ["hybrid", "Hybrid"]];

export default function ManageAlerts() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/job-alerts/manage/${token}`);
      setData(r.data);
    } catch (e) {
      setError(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (a) => {
    try {
      await api.put(`/job-alerts/manage/${token}/${a.id}`, { active: !a.active });
      toast.success(a.active ? "Alert paused" : "Alert resumed");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (a) => {
    if (!window.confirm("Delete this job alert? You'll stop getting emails for it.")) return;
    try {
      await api.delete(`/job-alerts/manage/${token}/${a.id}`);
      toast.success("Alert deleted");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveEdit = async (a, form) => {
    try {
      await api.put(`/job-alerts/manage/${token}/${a.id}`, {
        q: form.q, location: form.location, work_mode: form.work_mode,
        min_salary: form.min_salary ? Number(form.min_salary) : 0,
      });
      toast.success("Alert updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-12" data-testid="manage-alerts">
      <div className="flex items-center gap-3 mb-1">
        <BellRing size={24} className="text-[#002FA7]" />
        <h1 className="font-display font-black text-3xl tracking-tighter">Manage your job alerts</h1>
      </div>

      {loading ? (
        <div className="font-mono2 text-sm text-[#9CA3AF] flex items-center gap-2 py-16"><Loader2 size={18} className="animate-spin" /> Loading your alerts…</div>
      ) : error ? (
        <div className="border-2 border-[#FF3B30] p-8 mt-6 text-center" data-testid="manage-error">
          <X size={28} className="mx-auto text-[#FF3B30] mb-2" />
          <div className="font-display font-bold">This link isn't valid</div>
          <p className="text-sm text-[#4B5563] mt-1">{error}</p>
          <Link to="/jobs" className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 mt-5">Browse jobs <ArrowRight size={15} /></Link>
        </div>
      ) : (
        <>
          <p className="text-sm text-[#4B5563] mb-6 flex items-center gap-1.5"><Mail size={14} /> Alerts for <b>{data.email}</b></p>
          {data.alerts.length === 0 ? (
            <div className="border border-[#E5E7EB] p-12 text-center" data-testid="manage-empty">
              <BellOff size={28} className="mx-auto text-[#9CA3AF] mb-2" />
              <div className="font-display font-bold">No alerts left</div>
              <p className="text-sm text-[#9CA3AF] mt-1">Create a new one from the job board.</p>
              <Link to="/jobs" className="btn-outline px-5 py-2.5 text-sm font-medium inline-block mt-4">Go to job board</Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="manage-list">
              {data.alerts.map((a) => (
                <div key={a.id} data-testid={`alert-${a.id}`} className={`border p-5 hard-card ${a.active ? "border-[#0A0A0A]" : "border-[#E5E7EB] bg-[#F9FAFB]"}`}>
                  {editing === a.id ? (
                    <EditForm alert={a} onCancel={() => setEditing(null)} onSave={(form) => saveEdit(a, form)} />
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex-1 min-w-[180px]">
                        <div className="font-display font-bold flex items-center gap-2">
                          {a.label}
                          <span className={`font-mono2 text-[10px] uppercase px-1.5 py-0.5 ${a.active ? "bg-[#10B981] text-white" : "border border-[#9CA3AF] text-[#9CA3AF]"}`}>
                            {a.active ? "Active" : "Paused"}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button data-testid={`alert-toggle-${a.id}`} onClick={() => toggle(a)} className="btn-outline px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5">
                          {a.active ? <><BellOff size={13} /> Pause</> : <><BellRing size={13} /> Resume</>}
                        </button>
                        <button data-testid={`alert-edit-${a.id}`} onClick={() => setEditing(a.id)} className="btn-outline px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Pencil size={13} /> Edit</button>
                        <button data-testid={`alert-delete-${a.id}`} onClick={() => remove(a)} className="px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white transition-colors"><Trash2 size={13} /> Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-8 pt-6 border-t border-[#E5E7EB] text-center">
            <Link to="/jobs" data-testid="manage-browse" className="btn-ink px-6 py-3 text-sm font-medium inline-flex items-center gap-2">Browse & add a new alert <ArrowRight size={15} /></Link>
          </div>
        </>
      )}
    </div>
  );
}

function EditForm({ alert, onCancel, onSave }) {
  const c = alert.criteria || {};
  const [form, setForm] = useState({
    q: c.q || "", location: c.location || "", work_mode: c.work_mode || "", min_salary: c.min_salary || "",
  });
  return (
    <div className="space-y-3" data-testid="alert-edit-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="edit-q" value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} placeholder="Keyword (title, skill)" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        <input data-testid="edit-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        <select data-testid="edit-work-mode" value={form.work_mode} onChange={(e) => setForm({ ...form, work_mode: e.target.value })} className="border border-[#0A0A0A] px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          {WORK_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="edit-salary" type="number" value={form.min_salary} onChange={(e) => setForm({ ...form, min_salary: e.target.value })} placeholder="Min salary" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
      </div>
      <div className="flex gap-2">
        <button data-testid="edit-save" onClick={() => onSave(form)} className="btn-ink px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"><Check size={14} /> Save</button>
        <button data-testid="edit-cancel" onClick={onCancel} className="btn-outline px-4 py-2 text-sm font-medium">Cancel</button>
      </div>
    </div>
  );
}
